import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { CandleFlame, OpenJournal, SacredCircle, FamilyTree } from './SacredIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/4WWzHmFE89jHswx3WKYxhh/sin_fondo_CF,_2B_maestria.png',
  alt: 'Vademécum del Constelador Familiar™ + 4 Bonos + Maestría — Sistema profesional completo'
};
export const Hero = () => {
  return <div className="relative overflow-hidden bg-transparent pt-1 pb-8 md:pt-20 md:pb-12" data-id="element-112" data-mp="xa4ipgcuuhh6">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-violetLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-113" data-mp="s9zx5bx3awk3"></div>

      {/* Floating Sacred decor (desktop only) */}
      <CandleFlame className="hidden md:block absolute top-32 left-[6%] w-8 h-12 opacity-30 animate-float-soft -z-10" style={{
      transform: 'rotate(5deg)'
    }} data-id="element-114" data-mp="3cz5vmcnsk20v" />
      <SacredCircle className="hidden md:block absolute top-48 right-[8%] w-24 h-24 opacity-20 animate-float-soft -z-10" style={{
      transform: 'rotate(-15deg)',
      animationDelay: '1.2s'
    }} data-id="element-115" data-mp="3j6od1nctwsro" />
      <OpenJournal className="hidden md:block absolute bottom-40 left-[4%] w-20 h-16 opacity-30 animate-float-soft -z-10" style={{
      animationDelay: '2s'
    }} data-id="element-116" data-mp="3e8ms0wvuejdk" />
      <FamilyTree className="hidden md:block absolute bottom-32 right-[5%] w-12 h-12 opacity-40 animate-float-soft -z-10" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-117" data-mp="358zq89z7bbjz" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-118" data-mp="2vj7uhcfd8cdx">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent" data-id="element-119" data-mp="2hkgibvk2d0sf">
          {/* Badge */}
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-gold/30 rounded-full gap-1.5 shadow-sm px-2.5 py-0.5 md:px-3 md:py-1 mb-1.5 md:mb-6" data-id="element-120" data-mp="n9qhz7v3iq6w">
            <span className="text-[11px] md:text-sm" data-id="element-121" data-mp="deq6q9xkgtzv">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-[10px] md:text-sm text-brand-violetDark" data-id="element-122" data-mp="1b1ffd2mry4up">
              LUMEN AKASHA™
            </span>
          </div>

          {/* Headline */}
          <div className="mb-0.5 md:mb-4 max-w-[21rem] sm:max-w-3xl mx-auto" data-id="element-123" data-mp="111yd6v6qe93h">
            <h1 className="font-display text-[34px] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.98] tracking-[-0.025em] text-brand-violetDark text-center" data-id="element-124" data-mp="x1eehn6iexf8">
              Tené siempre una intervención lista para cada sesión.
            </h1>
          </div>

          {/* Hero Image — fills the full available mobile frame */}
          <div className="relative z-10 w-[calc(100%+2rem)] -ml-4 sm:w-full sm:max-w-xl sm:ml-0 md:max-w-2xl lg:max-w-3xl mx-auto mb-1 md:mb-3 flex justify-center" data-id="element-125" data-mp="1xama741tsf59">
            <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="w-full object-contain drop-shadow-2xl h-[241px]" draggable={false} data-id="element-126" data-mp="269hc43xu732r" />
          </div>

          {/* Short value prop — above the CTA */}
          <div className="max-w-2xl mx-auto mb-2 md:mb-4 px-2 space-y-0.5 md:space-y-1.5" data-id="element-127" data-mp="2kx2f7xupp2ao">
            <p className="md:text-2xl font-bold text-brand-violet font-serif text-[24px]" data-id="element-128" data-mp="2p10gae3lkn6d">
              🌟 El recurso N°1 para consteladores y terapeutas sistémicos en
              Argentina.
            </p>
            <p className="md:text-lg text-brand-text/80 font-sans text-[18px]" data-id="element-129" data-mp="uoaj9nhxmxur">
              ✨ Vademécum del Constelador Familiar™ — Sistema profesional
              completo.
            </p>
          </div>

          {/* Chips — moved above the CTA so the CTA sits lower while staying above the fold */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-3 mb-2.5 md:mb-6 text-xs md:text-sm font-medium px-2" data-id="element-130" data-mp="3vjrtcbj4nvs2">
            <div className="inline-flex items-center bg-white rounded-full border border-brand-violet/20 shadow-sm text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2 font-semibold text-brand-violetDark" data-id="element-131" data-mp="99xmnrbwu5m9">
              <BookOpen className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-violet shrink-0" data-id="element-132" data-mp="3crphddhfl3o5" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white rounded-full border border-brand-violet/20 shadow-sm text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2 font-semibold text-brand-violetDark" data-id="element-133" data-mp="3rheodpc7g1wc">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-violet shrink-0" data-id="element-134" data-mp="2yxoi8imkhen9" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white rounded-full border border-brand-violet/20 shadow-sm text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2 font-semibold text-brand-violetDark" data-id="element-135" data-mp="19ids8cweehte">
              <span className="mr-1 md:mr-1.5 text-brand-violet shrink-0" data-id="element-136" data-mp="zl95obyn4kc2">
                🌿
              </span>
              Terapeutas formados
            </div>
          </div>

          {/* CTA — as low as possible while staying above the fold on mobile */}
          <div className="flex flex-col items-center space-y-2 md:space-y-6 mb-3 md:mb-12" data-id="element-137" data-mp="1x883iq12jybj">
            <CTAButton variant="purple" className="text-[15px] md:text-2xl px-6 md:px-12 py-3.5 md:py-6 cta-shimmer relative overflow-hidden font-bold" data-id="element-138" data-mp="1x5voakmp4226">
              <span className="relative z-10 text-[16px]" data-id="element-139" data-mp="1nbwp9uhx4yeg">
                → QUIERO MI VADEMÉCUM AHORA
              </span>
            </CTAButton>

            {/* Compact mobile trust row */}
            <div className="flex md:hidden flex-wrap justify-center items-center gap-x-2.5 gap-y-1 text-[10px] text-brand-muted" data-id="element-140" data-mp="bssm1wz98kan">
              <div className="flex items-center" data-id="element-141" data-mp="29iqyn8s06g77">
                <Shield className="w-3 h-3 mr-1 text-brand-goldDark" data-id="element-142" data-mp="29j96z3rxwkvh" />
                Garantía 7 días
              </div>
              <span className="text-brand-gold/50" data-id="element-143" data-mp="24pedj8mj8rj7">·</span>
              <div className="flex items-center" data-id="element-144" data-mp="1zrwdk7qhtmnc">📥 Descarga inmediata</div>
              <span className="text-brand-gold/50" data-id="element-145" data-mp="1vrcid9phct3u">·</span>
              <div className="flex items-center" data-id="element-146" data-mp="1vopz4ct88p5s">
                <Lock className="w-3 h-3 mr-1 text-brand-goldDark" data-id="element-147" data-mp="1qqy7ljwhv524" /> Pago
                seguro
              </div>
            </div>
          </div>

          {/* Extra detail below the fold */}
          <div className="max-w-3xl mx-auto mb-6 md:mb-8 mt-2 md:mt-0 px-2" data-id="element-148" data-mp="1guld4jt4kn6b">
            <p className="text-[13px] md:text-lg text-brand-text/80 font-sans leading-snug" data-id="element-149" data-mp="3g6nvtrtxddix">
              📚 Preguntas poderosas + 💬 frases sanadoras + 🧭 protocolos de
              intervención + 🗂️ fichas de consulta rápida, listos para usar en
              tu próxima sesión.
            </p>
          </div>

          {/* Full trust badges (desktop only) */}
          <div className="hidden md:flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-150" data-mp="3noc93r0gxl80">
            <div className="flex items-center" data-id="element-151" data-mp="1bdrzrp70333">
              <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-152" data-mp="1a2olsznsxci" />{' '}
              Garantía 7 días
            </div>
            <div className="flex items-center" data-id="element-153" data-mp="34rb3c3z3bh30">📥 Descarga inmediata</div>
            <div className="flex items-center" data-id="element-154" data-mp="34tnlah4ulrg2">
              🖨️ Listo para imprimir o tablet
            </div>
            <div className="flex items-center" data-id="element-155" data-mp="3epqw0bdrmyem">
              <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-156" data-mp="3eqjuyq6hgjm7" />{' '}
              Pago seguro
            </div>
            <div className="flex items-center" data-id="element-157" data-mp="p2j6o3jecpbt">
              <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-158" data-mp="tyqey2z9mcwx" />{' '}
              Mercado Pago
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-8 md:mt-12 border-y border-brand-violet/15 bg-white/60 py-3 md:py-4 backdrop-blur-sm" data-id="element-159" data-mp="1b3a2xwm59r8t">
        <div className="md:hidden flex flex-wrap justify-center gap-x-4 gap-y-1 px-4 text-[11px]" data-id="element-160" data-mp="1ouoiftgufab4">
          <div className="flex items-center" data-id="element-161" data-mp="1k1c6ojx9ixzs">
            <span className="font-bold text-brand-violet mr-1" data-id="element-162" data-mp="1yr1nfnhu9al6">+200</span>{' '}
            terapeutas
          </div>
          <div className="flex items-center" data-id="element-163" data-mp="1tt9nc671ii5w">
            <span className="font-bold text-brand-violet mr-1" data-id="element-164" data-mp="1tuknlw1a17s9">1</span> Libro
          </div>
          <div className="flex items-center" data-id="element-165" data-mp="27q57ousqrw19">
            <span className="font-bold text-brand-violet mr-1" data-id="element-166" data-mp="22smswns1mmlc">4</span> Bonos
          </div>
          <div className="flex items-center" data-id="element-167" data-mp="ddos33e235jq">
            <Star className="w-3 h-3 text-brand-goldDark fill-brand-goldDark mr-0.5" data-id="element-168" data-mp="deqda0osd8f6" />{' '}
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-169" data-mp="2k2h4sfjcg9yl">
          <div className="flex items-center" data-id="element-170" data-mp="2p23dqcs5341l">
            <span className="font-bold text-brand-violet mr-2" data-id="element-171" data-mp="2p0s9kbus7u9y">+200</span>{' '}
            terapeutas equipados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-172" data-mp="2fbqxgwrcxx19"></div>
          <div className="flex items-center" data-id="element-173" data-mp="2f9nvdecxkdg6">
            <span className="font-bold text-brand-violet mr-2" data-id="element-174" data-mp="21bpu9anmedim">1</span> Libro
            incluido
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-175" data-mp="26c3wgc2jit8q"></div>
          <div className="flex items-center" data-id="element-176" data-mp="1sc2usuegbhs7">
            <span className="font-bold text-brand-violet mr-2" data-id="element-177" data-mp="1sddxeqw5ujhq">4</span> Bonos
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-178" data-mp="1wepnsymuz8qz"></div>
          <div className="flex items-center" data-id="element-179" data-mp="3mn7rtfv18vyk">
            <Star className="w-4 h-4 text-brand-goldDark fill-brand-goldDark mr-1" data-id="element-180" data-mp="3qr76dswr5kpa" />{' '}
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};